import React from 'react';
import ReactDOM from 'react-dom';
import { AppConfig } from './config.js';
import * as i18nModule from './utils/i18n.js';
import { escapeHtml } from './utils/security.js';
import VisualizeApp from './app.js';
import SettingsModal from './components/ui/SettingsModal.jsx';
import { appServices } from './services/AppServices.js';

// Production: alle Console-Ausgaben unterdrücken (zweite Ebene nach index.html)
(function () {
  try {
    if (typeof window === 'undefined') return;
    const host = window.location && window.location.hostname;
    const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
    const debug = (new URLSearchParams(window.location.search || '').get('debug') === '1') || (window.localStorage && window.localStorage.getItem('debugLogs') === 'true');
    if (isLocalhost || debug || typeof console === 'undefined') return;
    const noop = () => {};
    if (typeof console.log === 'function') console.log = noop;
    if (typeof console.info === 'function') console.info = noop;
    if (typeof console.debug === 'function') console.debug = noop;
    if (typeof console.warn === 'function') console.warn = noop;
    if (typeof console.error === 'function') console.error = noop;
  } catch (_) {}
})();

const i18n = i18nModule.i18n || i18nModule.default;

/** Aktualisiert alle Sidebar-Texte (Nav, Einstellungen, Abmelden, Loading) – funktioniert auch bei Vite (root index.html ohne Inline-Script). */
function updateSidebarTranslations() {
  if (typeof window === 'undefined' || !window.i18n || !window.i18n.t) return;
  const t = window.i18n.t.bind(window.i18n);
  const navKeys = {
    'data-sources': 'nav.dataSources',
    'mappings': 'nav.mappings',
    'calculations': 'nav.models',
    'reporting': 'nav.spaces'
  };
  Object.keys(navKeys).forEach((page) => {
    const link = document.querySelector(`[data-page="${page}"] .nav-text`);
    if (link) {
      const translated = t(navKeys[page]);
      if (translated && translated !== navKeys[page]) link.textContent = translated;
    }
  });
  const settingsEl = document.querySelector('#settings-open-btn span');
  if (settingsEl) {
    const translated = t('nav.settings');
    if (translated && translated !== 'nav.settings') settingsEl.textContent = translated;
  }
  const logoutEl = document.querySelector('#logout-btn span');
  if (logoutEl) {
    const translated = t('common.logout');
    if (translated && translated !== 'common.logout') logoutEl.textContent = translated;
  }
  const loadingEl = document.querySelector('.loading-state p');
  if (loadingEl) {
    const translated = t('common.loadingApplication');
    if (translated && translated !== 'common.loadingApplication') loadingEl.textContent = translated;
  }
}

if (typeof window !== 'undefined') {
  window.AppConfig = AppConfig;
  window.i18n = i18n;
  window.updateSidebarTranslations = updateSidebarTranslations;
  window.escapeHtml = escapeHtml;
  // Für app.js openSettingsModal() – erwartet React/ReactDOM/SettingsModal auf window
  window.React = React;
  window.ReactDOM = ReactDOM;
  window.SettingsModal = SettingsModal;
  window.appServices = appServices;

  appServices.register('AppConfig', AppConfig);
  appServices.register('i18n', i18n);
  appServices.register('updateSidebarTranslations', updateSidebarTranslations);
  appServices.register('escapeHtml', escapeHtml);
  appServices.register('React', React);
  appServices.register('ReactDOM', ReactDOM);
  appServices.register('SettingsModal', SettingsModal);
  appServices.register('localStorage', window.localStorage);
  appServices.register('window', window);

  // Sidebar sofort bei Sprachwechsel aktualisieren (wichtig bei Vite, wo public/index.html nicht geladen wird)
  window.addEventListener('languageChanged', () => {
    setTimeout(updateSidebarTranslations, 0);
  });
}

// Hide body until i18n is ready (replicate inline behaviour from old index.html)
const style = document.createElement('style');
style.textContent = 'body { visibility: hidden !important; }';
document.head.appendChild(style);

async function bootstrap() {
  try {
    const savedLanguage = localStorage.getItem('userLanguage') || 'de';
    const response = await fetch(`/translations/${savedLanguage}.json`);
    let translationsData;
    if (!response.ok) {
      const fallbackResponse = await fetch('/translations/de.json');
      if (!fallbackResponse.ok) {
        style.remove();
        await i18nModule.initI18n();
        mountApp();
        return;
      }
      translationsData = await fallbackResponse.json();
      i18n.translations.de = translationsData;
      i18n.currentLanguage = 'de';
    } else {
      translationsData = await response.json();
      const deResponse = await fetch('/translations/de.json');
      const enResponse = await fetch('/translations/en.json');
      const deTranslations = deResponse.ok ? await deResponse.json() : {};
      const enTranslations = enResponse.ok ? await enResponse.json() : {};
      i18n.translations = {
        [savedLanguage]: translationsData,
        de: deTranslations,
        en: enTranslations
      };
      i18n.currentLanguage = savedLanguage;
    }
    await i18nModule.initI18n();
  } catch (e) {
    console.error('i18n bootstrap error:', e);
  } finally {
    style.remove();
  }
  mountApp();
}

function mountApp() {
  const app = new VisualizeApp();
  if (typeof window !== 'undefined') window.app = app;
  app.init();

  // Sidebar-Logo dynamisch: Org-Logo wenn vorhanden, sonst Siqment-Fallback
  wireSidebarLogo();

  // Sidebar sofort in aktuelle Sprache bringen
  updateSidebarTranslations();
}

function wireSidebarLogo() {
  const logoImg = document.querySelector('.sidebar .logo-icon img');
  if (!logoImg) return;
  const fallbackSrc = logoImg.getAttribute('src');
  const fallbackAlt = logoImg.getAttribute('alt') || 'Logo';

  const applyLogo = (org) => {
    const logoUrl = org && org.logoUrl;
    if (logoUrl) {
      if (logoImg.getAttribute('src') !== logoUrl) {
        logoImg.setAttribute('src', logoUrl);
        logoImg.setAttribute('alt', `${org.name || ''} Logo`.trim());
      }
    } else {
      if (logoImg.getAttribute('src') !== fallbackSrc) {
        logoImg.setAttribute('src', fallbackSrc);
        logoImg.setAttribute('alt', fallbackAlt);
      }
    }
  };

  let attached = false;
  const tryAttach = () => {
    if (attached || !window.organizationService) return false;
    const current = window.organizationService.getCurrentOrganization?.();
    if (current !== undefined) applyLogo(current);
    window.organizationService.addListener?.(applyLogo);
    attached = true;
    return true;
  };

  if (!tryAttach()) {
    const intervalId = setInterval(() => {
      if (tryAttach()) clearInterval(intervalId);
    }, 200);
    setTimeout(() => clearInterval(intervalId), 15000);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
